import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Info, Brush } from "lucide-react";

const PERMANENT = {
  upperRight: ["18", "17", "16", "15", "14", "13", "12", "11"],
  upperLeft:  ["21", "22", "23", "24", "25", "26", "27", "28"],
  lowerRight: ["48", "47", "46", "45", "44", "43", "42", "41"],
  lowerLeft:  ["31", "32", "33", "34", "35", "36", "37", "38"],
};
const TEMPORAL = {
  upperRight: ["55", "54", "53", "52", "51"],
  upperLeft:  ["61", "62", "63", "64", "65"],
  lowerRight: ["85", "84", "83", "82", "81"],
  lowerLeft:  ["71", "72", "73", "74", "75"],
};

// Estados aplicables a una sola cara
const SURFACE_STATES = [
  { key: "sano",      label: "Sano",      color: "#EAEAE3", text: "#1A1C1A" },
  { key: "caries",    label: "Caries",    color: "#D9542B", text: "#fff" },
  { key: "obturado",  label: "Obturado",  color: "#1B4332", text: "#fff" },
  { key: "sellante",  label: "Sellante",  color: "#5BA3D0", text: "#fff" },
];
// Estados que afectan al diente completo
const TOOTH_STATES = [
  { key: "endodoncia", label: "Endodoncia", color: "#7A4FBA", text: "#fff" },
  { key: "corona",     label: "Corona",     color: "#E9B824", text: "#000" },
  { key: "implante",   label: "Implante",   color: "#1E3A8A", text: "#fff" },
  { key: "extraido",   label: "Extraído",   color: "#B42318", text: "#fff" },
  { key: "ausente",    label: "Ausente",    color: "transparent", text: "#888", dashed: true },
];
const ALL_STATES = [...SURFACE_STATES, ...TOOTH_STATES];
const STATE_MAP = Object.fromEntries(ALL_STATES.map((s) => [s.key, s]));
const TOOTH_KEYS = new Set(TOOTH_STATES.map((s) => s.key));

const SURFACE_LABELS = {
  oclusal: "Oclusal / Incisal",
  vestibular: "Vestibular",
  palatino: "Palatina",
  lingual: "Lingual",
  mesial: "Mesial",
  distal: "Distal",
};

// Para cada diente: la cara izquierda/derecha es mesial o distal según cuadrante
function quadrantOf(n) {
  return n[0]; // "1","2","3","4","5","6","7","8"
}
function isUpper(n) { return ["1", "2", "5", "6"].includes(quadrantOf(n)); }
function innerSurface(n) { return isUpper(n) ? "palatino" : "lingual"; }
// Mesial está hacia la línea media. Cuadrantes 1,4 (derecha del paciente) → mesial=right
// Cuadrantes 2,3 (izquierda del paciente) → mesial=left
function leftSurfaceOf(n) {
  const q = quadrantOf(n);
  return ["1", "4", "5", "8"].includes(q) ? "distal" : "mesial";
}
function rightSurfaceOf(n) {
  const q = quadrantOf(n);
  return ["1", "4", "5", "8"].includes(q) ? "mesial" : "distal";
}

const EMPTY_SURFACES = () => ({ oclusal: "sano", vestibular: "sano", palatino: "sano", lingual: "sano", mesial: "sano", distal: "sano" });

// Migra la data antigua al nuevo formato
function migrate(old) {
  if (!old) return { whole_status: "sano", surfaces: EMPTY_SURFACES(), notes: "" };
  if (old.surfaces || old.whole_status) {
    return {
      whole_status: old.whole_status || "sano",
      surfaces: { ...EMPTY_SURFACES(), ...(old.surfaces || {}) },
      notes: old.notes || "",
    };
  }
  // Formato legacy: {status, notes}
  const s = old.status || "sano";
  if (TOOTH_KEYS.has(s)) {
    return { whole_status: s, surfaces: EMPTY_SURFACES(), notes: old.notes || "" };
  }
  // Si era surface-level, aplicarlo solo a oclusal
  return {
    whole_status: "sano",
    surfaces: { ...EMPTY_SURFACES(), oclusal: s },
    notes: old.notes || "",
  };
}

function ToothSurface({ n, data, onPaint, onSelect, selected }) {
  const inner = innerSurface(n);
  const leftS = leftSurfaceOf(n);
  const rightS = rightSurfaceOf(n);

  const surfData = data.surfaces || {};
  const whole = data.whole_status || "sano";

  // Si el diente tiene whole_status no-sano, todas las caras se pintan con su color base
  const wholeMeta = STATE_MAP[whole] || STATE_MAP.sano;
  const ausente = whole === "ausente";
  const extraido = whole === "extraido";
  const corona = whole === "corona";
  const overrideAll = ["endodoncia", "implante"].includes(whole);

  const colorOf = (surfaceKey) => {
    if (ausente) return "transparent";
    if (overrideAll || corona) return wholeMeta.color;
    return STATE_MAP[surfData[surfaceKey] || "sano"].color;
  };

  const stroke = selected ? "#1B4332" : "#A0A39E";
  const sw = selected ? 1.8 : 0.8;

  const polys = [
    { key: "top",    surf: "vestibular",            d: "0,0 50,0 34,16 16,16" },
    { key: "right",  surf: rightS,                  d: "50,0 50,50 34,34 34,16" },
    { key: "bottom", surf: inner,                   d: "50,50 0,50 16,34 34,34" },
    { key: "left",   surf: leftS,                   d: "0,50 0,0 16,16 16,34" },
    { key: "center", surf: "oclusal",               d: "16,16 34,16 34,34 16,34" },
  ];

  const handleClick = (e, surf) => {
    e.stopPropagation();
    onPaint(n, surf);
  };

  return (
    <button
      type="button"
      data-testid={`tooth-${n}`}
      onClick={() => onSelect(n)}
      title={`Diente ${n}`}
      className="group flex flex-col items-center"
    >
      <svg viewBox="0 0 50 50" width="42" height="42" className="cursor-pointer">
        {polys.map((p) => (
          <polygon
            key={p.key}
            data-testid={`tooth-${n}-${p.surf}`}
            points={p.d}
            fill={colorOf(p.surf)}
            stroke={stroke}
            strokeWidth={sw}
            strokeDasharray={ausente ? "2 2" : ""}
            onClick={(e) => handleClick(e, p.surf)}
            className="hover:opacity-80 transition-opacity"
          >
            <title>{`Diente ${n} · ${SURFACE_LABELS[p.surf]}`}</title>
          </polygon>
        ))}
        {extraido && (
          <g stroke="#B42318" strokeWidth="3">
            <line x1="3" y1="3" x2="47" y2="47" />
            <line x1="47" y1="3" x2="3" y2="47" />
          </g>
        )}
        {corona && (
          <rect x="2" y="2" width="46" height="46" fill="none" stroke="#E9B824" strokeWidth="2.5" />
        )}
        {whole === "implante" && (
          <g stroke="#fff" strokeWidth="1.5">
            <line x1="25" y1="14" x2="25" y2="36" />
            <line x1="20" y1="20" x2="30" y2="20" />
            <line x1="20" y1="26" x2="30" y2="26" />
            <line x1="20" y1="32" x2="30" y2="32" />
          </g>
        )}
      </svg>
      <span className={`text-[10px] font-mono mt-0.5 ${selected ? "font-bold text-primary" : "text-muted-foreground"}`}>{n}</span>
    </button>
  );
}

export default function Odontograma({ patientId }) {
  const [denticion, setDenticion] = useState("permanente");
  const [teeth, setTeeth] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [brush, setBrush] = useState("caries");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/patients/${patientId}/odontograma`).then((r) => {
      const raw = r.data.teeth || {};
      const mig = {};
      for (const k of Object.keys(raw)) mig[k] = migrate(raw[k]);
      setTeeth(mig);
      setUpdatedAt(r.data.updated_at || null);
    }).catch(() => {});
  }, [patientId]);

  const paint = (n, surf) => {
    setTeeth((t) => {
      const cur = t[n] || migrate(null);
      if (TOOTH_KEYS.has(brush)) {
        return { ...t, [n]: { ...cur, whole_status: brush === cur.whole_status ? "sano" : brush } };
      }
      // surface brush
      const surfs = { ...(cur.surfaces || EMPTY_SURFACES()), [surf]: brush };
      return { ...t, [n]: { ...cur, whole_status: cur.whole_status === "sano" ? "sano" : cur.whole_status, surfaces: surfs } };
    });
    setDirty(true);
  };

  const setNotes = (n, notes) => {
    setTeeth((t) => ({ ...t, [n]: { ...(t[n] || migrate(null)), notes } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/patients/${patientId}/odontograma`, { teeth });
      setUpdatedAt(data.updated_at);
      setDirty(false);
      toast.success("Odontograma guardado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const counts = useMemo(() => {
    const c = {};
    for (const k of Object.keys(teeth)) {
      const t = teeth[k];
      if (t.whole_status && t.whole_status !== "sano") {
        c[t.whole_status] = (c[t.whole_status] || 0) + 1;
      }
      const surfs = t.surfaces || {};
      for (const sk of Object.keys(surfs)) {
        if (surfs[sk] && surfs[sk] !== "sano") c[surfs[sk]] = (c[surfs[sk]] || 0) + 1;
      }
    }
    return c;
  }, [teeth]);

  const arch = denticion === "permanente" ? PERMANENT : TEMPORAL;

  const renderRow = (leftQ, rightQ) => (
    <div className="flex items-end justify-center gap-2 sm:gap-3">
      <div className="flex gap-1 sm:gap-1.5">
        {leftQ.map((n) => (
          <ToothSurface key={n} n={n}
            data={teeth[n] || migrate(null)}
            onPaint={paint}
            onSelect={setSelected}
            selected={selected === n}
          />
        ))}
      </div>
      <div className="w-px self-stretch bg-border" />
      <div className="flex gap-1 sm:gap-1.5">
        {rightQ.map((n) => (
          <ToothSurface key={n} n={n}
            data={teeth[n] || migrate(null)}
            onPaint={paint}
            onSelect={setSelected}
            selected={selected === n}
          />
        ))}
      </div>
    </div>
  );

  const sel = selected ? (teeth[selected] || migrate(null)) : null;

  return (
    <div className="space-y-5" data-testid="odontograma">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Odontograma</h2>
          <p className="text-sm text-muted-foreground">FDI · 5 caras por diente. Selecciona el estado abajo y haz click en la cara del diente.</p>
          {updatedAt && <div className="text-xs text-muted-foreground font-mono mt-1">Última actualización: {new Date(updatedAt).toLocaleString("es-CO")}</div>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-sm overflow-hidden">
            <button onClick={() => setDenticion("permanente")} data-testid="tab-permanente"
              className={`px-4 h-10 text-xs font-bold uppercase tracking-widest ${denticion === "permanente" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>Permanente</button>
            <button onClick={() => setDenticion("temporal")} data-testid="tab-temporal"
              className={`px-4 h-10 text-xs font-bold uppercase tracking-widest ${denticion === "temporal" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>Temporal</button>
          </div>
          <button onClick={save} disabled={!dirty || saving} className="btn-primary disabled:opacity-50" data-testid="odontograma-save">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>

      {/* Brush toolbar */}
      <div className="card-flat p-3 space-y-3" data-testid="brush-toolbar">
        <div className="flex items-center gap-2 text-xs">
          <Brush className="w-4 h-4 text-primary" />
          <span className="label-eyebrow">Pincel activo</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground self-center mr-1">Por cara:</div>
          {SURFACE_STATES.map((s) => (
            <button key={s.key} onClick={() => setBrush(s.key)} data-testid={`brush-${s.key}`}
              className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border ${brush === s.key ? "ring-2 ring-primary scale-105" : "hover:opacity-80"}`}
              style={{ background: s.color, color: s.text, borderColor: "hsl(var(--border))" }}>
              {s.label}
            </button>
          ))}
          <div className="w-px self-stretch bg-border mx-1" />
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground self-center mr-1">Diente entero:</div>
          {TOOTH_STATES.map((s) => (
            <button key={s.key} onClick={() => setBrush(s.key)} data-testid={`brush-${s.key}`}
              className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border ${brush === s.key ? "ring-2 ring-primary scale-105" : "hover:opacity-80"}`}
              style={{ background: s.color, color: s.text, borderColor: "hsl(var(--border))", borderStyle: s.dashed ? "dashed" : "solid" }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card-flat p-4 sm:p-6 space-y-4 overflow-x-auto">
        <div className="text-center text-[10px] uppercase tracking-widest font-bold text-muted-foreground">FDI · {denticion}</div>
        {renderRow(arch.upperRight, arch.upperLeft)}
        <div className="h-px bg-border" />
        {renderRow(arch.lowerRight, arch.lowerLeft)}
      </div>

      {/* Counts legend */}
      <div className="flex flex-wrap gap-2">
        {ALL_STATES.filter((s) => s.key !== "sano").map((s) => (
          <div key={s.key} className="flex items-center gap-2 px-2 py-1 rounded-sm border border-border text-xs">
            <div className="w-3 h-3 rounded-sm border" style={{ background: s.color, borderColor: "hsl(var(--border))", borderStyle: s.dashed ? "dashed" : "solid" }} />
            <span>{s.label}</span>
            {counts[s.key] ? <span className="font-mono text-muted-foreground">· {counts[s.key]}</span> : null}
          </div>
        ))}
      </div>

      {/* Detail panel */}
      {selected ? (
        <div className="card-flat p-5 space-y-4" data-testid="tooth-detail-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-4">
              <ToothSurface n={selected} data={sel} onPaint={paint} onSelect={() => {}} selected />
              <div>
                <div className="label-eyebrow">Diente seleccionado</div>
                <div className="font-display font-black text-3xl tracking-tighter">#{selected}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Cara superior: <b>Vestibular</b> · inferior: <b>{innerSurface(selected) === "palatino" ? "Palatina" : "Lingual"}</b>
                </div>
              </div>
            </div>
            <button onClick={() => setSelected(null)} className="text-xs text-muted-foreground hover:text-foreground">Cerrar</button>
          </div>

          {/* Surface summary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {["oclusal", "vestibular", innerSurface(selected), "mesial", "distal"].map((sk) => {
              const st = sel.surfaces?.[sk] || "sano";
              const meta = STATE_MAP[st] || STATE_MAP.sano;
              return (
                <div key={sk} className="border border-border rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{SURFACE_LABELS[sk]}</div>
                  <div className="mt-1 inline-block text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded-sm" style={{ background: meta.color, color: meta.text }}>
                    {meta.label}
                  </div>
                </div>
              );
            })}
          </div>

          {sel.whole_status && sel.whole_status !== "sano" && (
            <div className="border border-primary/30 bg-primary/5 rounded-sm p-3 text-sm flex items-center justify-between gap-3">
              <div>Estado del diente completo: <b>{STATE_MAP[sel.whole_status]?.label}</b></div>
              <button
                onClick={() => setTeeth((t) => ({ ...t, [selected]: { ...(t[selected] || migrate(null)), whole_status: "sano" } })) || setDirty(true)}
                className="text-xs text-primary underline">
                Quitar
              </button>
            </div>
          )}

          <div>
            <div className="label-eyebrow mb-2">Notas del diente</div>
            <textarea rows={2} className="input-flat h-auto py-2" value={sel.notes || ""}
              onChange={(e) => setNotes(selected, e.target.value)}
              placeholder="Observaciones específicas para este diente…"
            />
          </div>
        </div>
      ) : (
        <div className="card-flat p-4 flex items-center gap-3 text-sm text-muted-foreground">
          <Info className="w-4 h-4 shrink-0" /> Selecciona un pincel arriba y haz click en una cara del diente para registrar su estado.
        </div>
      )}
    </div>
  );
}
